// meta-cache.ts — DEM-1933 (dashboard perf): dedup de listAgents/listSkills
// entre os consumidores do MOUNT da dashboard.
//
// Antes: DashboardPage (fallback de counts) e mc-store.hydrateActivityMeta
// chamavam api.listAgents() + api.listSkills() CADA UM — 2x as mesmas
// requests no mesmo mount. Este módulo é o ponto único: cache em módulo com
// TTL curto + dedup de in-flight (consumidores no mesmo tick compartilham a
// MESMA promise — zero request duplicada).
//
// TTL de 60s: menor que o META_REFRESH_MS (5min) do mc-store, então o refresh
// periódico do feed continua refetchando de verdade.

import { api, type AgentListItem, type Skill } from './api';

const TTL_MS = 60_000;

interface CacheSlot<T> {
  at: number;
  value: T;
}

let agentsSlot: CacheSlot<AgentListItem[]> | null = null;
let skillsSlot: CacheSlot<Skill[]> | null = null;
let agentsInflight: Promise<AgentListItem[]> | null = null;
let skillsInflight: Promise<Skill[]> | null = null;

/** listAgents com cache TTL 60s + dedup de in-flight. Erro NÃO é cacheado. */
export function listAgentsCached(): Promise<AgentListItem[]> {
  if (agentsSlot && Date.now() - agentsSlot.at < TTL_MS) {
    return Promise.resolve(agentsSlot.value);
  }
  if (agentsInflight) return agentsInflight;
  agentsInflight = api
    .listAgents()
    .then((value) => {
      agentsSlot = { at: Date.now(), value };
      return value;
    })
    .finally(() => {
      agentsInflight = null;
    });
  return agentsInflight;
}

/** listSkills com cache TTL 60s + dedup de in-flight. Erro NÃO é cacheado. */
export function listSkillsCached(): Promise<Skill[]> {
  if (skillsSlot && Date.now() - skillsSlot.at < TTL_MS) {
    return Promise.resolve(skillsSlot.value);
  }
  if (skillsInflight) return skillsInflight;
  skillsInflight = api
    .listSkills()
    .then((value) => {
      skillsSlot = { at: Date.now(), value };
      return value;
    })
    .finally(() => {
      skillsInflight = null;
    });
  return skillsInflight;
}

/** Test-only: zera cache + in-flight (testes com mocks por-case). */
export function resetMetaCache(): void {
  agentsSlot = null;
  skillsSlot = null;
  agentsInflight = null;
  skillsInflight = null;
}
